import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { useStripe } from '@stripe/react-stripe-js';
import { changeOwnCompanyData } from 'API/company';
import { Period, Product, SinglePrice, createCheckoutSession, offerPromotionCode } from 'API/payment';
import { Products } from 'enums';
import { Company } from 'types/company.types';
import CompanyInfoMissingDialog from 'features/payment/company-info-missing-dialog';
import { CompanyErrors, Currencies, CurrencySymbols } from 'features/payment/payment.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiConfirmDialog from 'features/ui/base-dialogs/pai-confirm-dialog';
import useOwnCompany from 'hooks/use-own-company';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { API_V1 } from 'API/APIs/APIv1';
import { getAccountTabRoute } from 'features/account/account-management/account-management-tabs';
import { AccountManagementTabs } from 'features/account/account-management/account-management.types';
import { useTranslation } from 'react-i18next';

import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import usePrices from '../features/payment/use-prices';

type BillingPeriod = Period.MONTH | Period.QUARTER | Period.YEAR;

const PERIOD_TO_API: Record<BillingPeriod, 'monthly' | 'quarterly' | 'yearly'> = {
  [Period.MONTH]: 'monthly',
  [Period.QUARTER]: 'quarterly',
  [Period.YEAR]: 'yearly',
};

const PERIOD_DIVISOR: Record<BillingPeriod, number> = {
  [Period.MONTH]: 1,
  [Period.QUARTER]: 3,
  [Period.YEAR]: 12,
};

interface ProductCard {
  productKey: Products;
  productName: string;
  tagline: string;
  features: string[];
  highlight?: { label: string; subline: string };
}

const PRODUCT_CARDS: ProductCard[] = [
  {
    productKey: Products.SAIA_RECRUITING,
    productName: 'Recruiting',
    tagline: 'Dein KI-Agent für Active Sourcing auf LinkedIn.',
    features: [
      'KI-basierte Talentsuche',
      'Psychologische Personalisierung',
      'Auto-Antworten & Vorqualifizierung',
      'Interview-Terminierung auf Wunsch',
      'Executive Summary',
      'CRM-Sync & Analytics',
    ],
  },
  {
    productKey: Products.SAIA_BUNDLE,
    productName: 'Recruiting + Sales Bundle',
    tagline: 'Recruiting- und Sales-Agent kombiniert.',
    features: [
      'Alles aus Recruiting',
      'Alles aus Sales',
      'Lead-Generierung & B2B-Outreach',
      'Multi-Channel Sequenzen',
      'Selbstlernende KI-Optimierung',
      '100% DSGVO-konform',
    ],
    highlight: {
      label: 'Über 10% sparen',
      subline: 'beide Agenten zum Vorteilspreis',
    },
  },
  {
    productKey: Products.SAIA_SALES,
    productName: 'Sales',
    tagline: 'Dein KI-Agent für Lead-Generierung und B2B-Outreach.',
    features: [
      'KI-basiertes Lead Sourcing',
      'Psychologische Personalisierung',
      'Automatisierte Sales-Sequenzen',
      'Prospect Enrichment',
      'Pipeline-Management',
      'CRM-Sync & Analytics',
    ],
  },
];

const Payment: React.FC = () => {
  const { t } = useTranslation();
  useUserData();
  const stripe = useStripe();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const { ownCompany, isCompanyMissing, companyInfo, handleCompanyInfoChange, handleCountryChange, fetchOwnCompany, fetchCountries, countryOptions } =
    useOwnCompany();
  const { prices, loading, fetchPrices } = usePrices();

  const currency = prices?.currency as Currencies | undefined;
  const currencySymbol = currency ? CurrencySymbols[currency] : '€';

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(Period.YEAR);
  const [pendingProduct, setPendingProduct] = useState<Products | null>(null);

  const [companyInfoMissingDialog, setCompanyInfoMissingDialog] = useState<{
    open: boolean;
    loading: boolean;
    error?: CompanyErrors;
  }>({ open: false, loading: false, error: undefined });
  const [validationDialog, setValidationDialog] = useState<{ open: boolean }>({ open: false });

  const { classes, cx } = useStyles();

  const handleOpenCompanyInfoMissingDialog = (error: CompanyErrors | undefined) => {
    setCompanyInfoMissingDialog((prev) => ({ ...prev, open: true, error }));
    fetchCountries();
  };

  const handleCloseCompanyInfoMissingDialog = () => {
    setCompanyInfoMissingDialog({ open: false, loading: false });
  };

  const handleUpdateCompanyInfo = async () => {
    if (_.isNil(ownCompany)) return;
    const updatePayload: Company = new Company({
      name: companyInfo.name,
      addressLine1: companyInfo.street,
      postalCode: companyInfo.zip,
      city: companyInfo.city,
      country: companyInfo.country?.value,
    });
    setCompanyInfoMissingDialog((prev) => ({ ...prev, loading: true }));
    const res = await changeOwnCompanyData(updatePayload);
    if (res && !res.error) {
      await fetchOwnCompany();
      fetchPrices();
      handleCloseCompanyInfoMissingDialog();
    } else {
      setCompanyInfoMissingDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res?.error, '/tenants/:tenantId', 'patch');
    }
  };

  React.useEffect(() => {
    if (isCompanyMissing) {
      handleOpenCompanyInfoMissingDialog(CompanyErrors.COMPANY_ADDRESS_MISSING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownCompany, isCompanyMissing]);

  const navigate = useNavigate();

  const handleStartCheckout = (productKey: Products) => async () => {
    setPendingProduct(productKey);
    try {
      // Läuft schon ein Abo, den Seat dort nachbuchen statt ein zweites Abo anzulegen
      // (im Testzeitraum bis zu dessen Ende gratis). Nur ohne Abo (409) geht es zum Checkout.
      try {
        await API_V1.post('/payments/seats', { seats: [{ product: productKey, quantity: 1 }] });
        // Die Lizenz legt der Stripe-Webhook an — kurz warten, dann zur Übersicht
        await new Promise((resolve) => setTimeout(resolve, 3000));
        navigate(getAccountTabRoute(AccountManagementTabs.LICENSES));
        return;
      } catch (e: any) {
        if (e?.response?.status !== 409) {
          handleOpenErrorDialog(e?.response?.data ?? e, '/payments/seats', 'post');
          return;
        }
      }
      const clientReferenceId =
        ((window as Window & { tolt_referral?: string; Rewardful?: { referral?: string } }).tolt_referral ??
          (window as Window & { Rewardful?: { referral?: string } }).Rewardful?.referral) ||
        'checkout_' + new Date().getTime();
      const products: Product[] = [{ product: productKey, quantity: 1 }];
      const dynamicRoute = `${window.location.origin}/dashboard`;
      const response = await createCheckoutSession(
        PERIOD_TO_API[billingPeriod] as never,
        products,
        undefined,
        clientReferenceId,
        dynamicRoute,
        currency,
        offerPromotionCode(),
      );
      if (!_.isNil(response.error)) {
        if (response.error.code === 300) {
          handleOpenCompanyInfoMissingDialog(CompanyErrors.COMPANY_NAME_TOO_LONG);
        } else {
          handleOpenErrorDialog(response?.error, '/payments/checkout-session', 'post');
        }
      } else if (response.sessionId && stripe) {
        stripe.redirectToCheckout({ sessionId: response.sessionId });
      }
    } finally {
      setPendingProduct(null);
    }
  };

  const renderCard = (card: ProductCard) => {
    const productPrices = (prices?.[card.productKey] ?? {}) as SinglePrice;
    const cyclePriceCents =
      billingPeriod === Period.YEAR
        ? productPrices.yearly
        : billingPeriod === Period.QUARTER
          ? productPrices.quarterly
          : productPrices.monthly;
    const monthlyEquivalent = cyclePriceCents !== undefined
      ? cyclePriceCents / PERIOD_DIVISOR[billingPeriod] / 100
      : undefined;

    const billingLabel =
      billingPeriod === Period.YEAR
        ? t('billedYearly')
        : billingPeriod === Period.QUARTER
          ? t('billedQuarterly')
          : t('billedMonthly');

    // Display-only: percentage saved compared to the monthly billing cycle.
    const monthlyReference = productPrices.monthly !== undefined ? productPrices.monthly / 100 : undefined;
    const savingPercent =
      monthlyEquivalent !== undefined && monthlyReference !== undefined && monthlyReference > 0
        ? Math.round((1 - monthlyEquivalent / monthlyReference) * 100)
        : 0;

    const isHighlighted = !!card.highlight;
    const isLoadingThis = pendingProduct === card.productKey;

    return (
      <Stack key={card.productKey} className={cx(classes.card, isHighlighted ? 'highlighted' : undefined)} gap={2.5}>
        {card.highlight ? <div className={classes.highlightBadge}>{card.highlight.label}</div> : null}

        <Stack gap={0.75}>
          <Typography className={classes.productName}>{card.productName}</Typography>
          <Typography className={classes.tagline}>{card.tagline}</Typography>
        </Stack>

        <Stack gap={0.5} className={classes.priceBlock}>
          <Stack direction={'row'} alignItems={'flex-end'} gap={0.75}>
            <Typography className={classes.price}>
              {monthlyEquivalent !== undefined ? monthlyEquivalent.toFixed(2) : '—'}
            </Typography>
            <Typography className={classes.currency}>{currencySymbol}</Typography>
            <Typography className={classes.cycleLabel}>{t('perMonthPerLicense')}</Typography>
          </Stack>
          <Typography className={classes.billingLabel}>{billingLabel}</Typography>
          {savingPercent > 0 ? (
            <Typography className={classes.saving}>{t('savedComparedToMonthly', { percent: savingPercent })}</Typography>
          ) : card.highlight ? (
            <Typography className={classes.saving}>{card.highlight.subline}</Typography>
          ) : null}
        </Stack>

        <Stack gap={1.25} className={classes.features}>
          {card.features.map((feature) => (
            <Stack key={feature} direction={'row'} gap={1.25} alignItems={'flex-start'}>
              <CheckCircleIcon className={classes.checkmark} />
              <Typography className={classes.feature}>{feature}</Typography>
            </Stack>
          ))}
        </Stack>

        <div className={cx(classes.cta, isHighlighted ? undefined : 'plain')}>
          <PaiButton
            text={isHighlighted ? t('chooseBundle') : t('chooseLicense')}
            border="rounded"
            size="medium"
            fullWidth={true}
            loading={isLoadingThis}
            action={handleStartCheckout(card.productKey)}
          />
        </div>
      </Stack>
    );
  };

  return (
    <div className={classes.container}>
      <Stack className={classes.headline} direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={4}>
        <Stack gap={1}>
          <Typography className={classes.title}>{t('chooseLicense')}</Typography>
          <Typography className={classes.subline}>{t('pricingVatNote')}</Typography>
        </Stack>

        <Stack className={classes.periodSwitch} direction={'row'}>
          {([
            { key: Period.MONTH, label: t('billingMonthly') },
            { key: Period.QUARTER, label: t('billingQuarterly') },
            { key: Period.YEAR, label: t('billingYearly') },
          ] as Array<{ key: BillingPeriod; label: string }>).map((option) => (
            <ButtonBase
              key={option.key}
              disableRipple
              onClick={() => setBillingPeriod(option.key)}
              className={cx(classes.periodToggle, billingPeriod === option.key ? 'active' : undefined)}
            >
              <Typography className={classes.periodLabel}>{option.label}</Typography>
            </ButtonBase>
          ))}
        </Stack>
      </Stack>

      <Stack gap={4}>
        {loading || _.isNil(prices) ? (
          <PaiDotLoader />
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems={'stretch'}>
            {PRODUCT_CARDS.map(renderCard)}
          </Stack>
        )}

        {!loading && !_.isNil(prices) ? (
          <Stack className={classes.contactCard} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
            <Typography className={classes.contactText}>{t('specialPlanContactIntro')}</Typography>
            <ButtonBase
              disableRipple
              className={classes.contactChip}
              onClick={() => window.open('mailto:toni.kuenzl@secret-ai-agent.de', '_blank')}
            >
              toni.kuenzl@secret-ai-agent.de
            </ButtonBase>
          </Stack>
        ) : null}
      </Stack>

      <CompanyInfoMissingDialog
        open={companyInfoMissingDialog.open}
        loading={companyInfoMissingDialog.loading}
        error={companyInfoMissingDialog.error}
        companyInfo={companyInfo}
        countryOptions={countryOptions}
        handleCompanyInfoChange={handleCompanyInfoChange}
        handleCountryChange={handleCountryChange}
        handleClose={handleCloseCompanyInfoMissingDialog}
        handleUpdateCompanyInfo={handleUpdateCompanyInfo}
      />
      <PaiConfirmDialog
        open={validationDialog.open}
        title={t('warningOnlyIntegers')}
        content={t('warningOnlyIntegersContent')}
        handleConfirm={() => setValidationDialog({ open: false })}
        handleClose={() => setValidationDialog({ open: false })}
      />
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: '40px 48px 48px',
    maxWidth: 1440,
    margin: '0 auto',
    background: theme.palette.backgrounds.main_new,
    borderRadius: 6,
    [theme.breakpoints.down('md')]: {
      padding: '24px 16px 32px',
    },
  },
  headline: {
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  title: {
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 500,
    letterSpacing: '-0.3px',
  },
  subline: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  periodSwitch: {
    flexShrink: 0,
    padding: 4,
    background: '#FFF',
    borderRadius: 999,
    border: `1px solid ${theme.palette.greys.lighter}`,
    flexWrap: 'wrap',
  },
  periodToggle: {
    padding: '10px 20px',
    borderRadius: 999,
    background: 'transparent',
    color: theme.palette.greys.dark,
    transition: 'all 120ms ease-in-out',
    '&.active': {
      background: theme.palette.primary.main,
      color: '#FFF',
    },
  },
  periodLabel: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: 'inherit',
    whiteSpace: 'nowrap',
  },
  card: {
    position: 'relative',
    flex: '1 1 0',
    minWidth: 0,
    padding: '32px 28px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 16,
    background: '#FFF',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    '&.highlighted': {
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
    },
  },
  highlightBadge: {
    position: 'absolute',
    top: -13,
    left: 28,
    padding: '5px 12px',
    borderRadius: 999,
    background: theme.palette.primary.main,
    color: '#FFF',
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    letterSpacing: '0.04em',
  },
  productName: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
  },
  tagline: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.dark,
    minHeight: 40,
  },
  priceBlock: {
    paddingBottom: 20,
    borderBottom: '1px solid #F0F0F5',
  },
  price: {
    fontSize: theme.typography.pxToRem(40),
    fontWeight: 700,
    lineHeight: 1,
  },
  currency: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
    paddingBottom: 4,
  },
  cycleLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    paddingBottom: 6,
  },
  billingLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  saving: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: theme.palette.success.main,
  },
  features: {
    flex: 1,
  },
  feature: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.4,
  },
  checkmark: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.primary.main,
    marginTop: 2,
    flexShrink: 0,
  },
  cta: {
    '& .MuiButtonBase-root': {
      height: 48,
      borderRadius: 10,
      padding: '0 16px',
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 700,
    },
    '&.plain .MuiButtonBase-root': {
      background: '#FFF',
      color: theme.palette.primary.main,
      border: '1px solid rgba(79,68,255,0.5)',
      '&:hover': {
        background: theme.palette.backgrounds.main,
      },
    },
  },
  contactCard: {
    background: '#FFF',
    borderRadius: 12,
    padding: '20px 24px',
  },
  contactText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.dark,
    maxWidth: 640,
  },
  contactChip: {
    marginLeft: 'auto',
    height: 40,
    padding: '0 18px',
    borderRadius: 8,
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    '&:hover': {
      background: '#E2E0FF',
    },
  },
}));

export default Payment;
